import FilterSelect from "../../components/ui/FilterSelect";
import StatusAction from "../../components/ui/StatusAction";
import { enumLabel } from "../../components/ui/enumLabel";
import "./CustomerShipmentsList.css";
import Button from "../../components/ui/Button";
import DatePresetFilter from "../../components/ui/DatePresetFilter";
import Pagination from "../../components/ui/Pagination";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getShipments, updateShipmentStatus } from "../../services/shipmentService";
import { Shipment } from "../../types/shipment";
import { SHIPMENT_STATUSES } from "../../constants/shipmentStatuses";
import { getApiErrorMessage } from "../../utils/apiError";
import { getCustomerDisplayName } from "../../utils/customerDisplay";
import { useAccess } from "../../hooks/useAccess";

export default function CustomerShipmentsList({ searchTerm }: { searchTerm: string }) {
  const { can } = useAccess();
  const [rows, setRows] = useState<Shipment[]>([]);
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [version, setVersion] = useState(0);
  useEffect(() => { setPage(1); }, [searchTerm]);
  useEffect(() => {
    const controller = new AbortController(); setLoading(true); setError("");
    const timer = setTimeout(async () => {
      try {
        if (startDate && endDate && startDate > endDate) throw new Error("The end date must be on or after the start date.");
        const result = await getShipments(page, 20, { search: searchTerm, status, startDate, endDate }, controller.signal);
        if (!controller.signal.aborted) { setRows(result.data.shipments); setPages(Math.max(1, result.data.pagination.totalPages)); setTotal(result.data.pagination.totalItems); }
      } catch (err) { if (!controller.signal.aborted) setError(getApiErrorMessage(err)); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [page, searchTerm, status, startDate, endDate, version]);
  const changeStatus = async (shipment: Shipment, newStatus: string) => {
    setActionError("");
    try { await updateShipmentStatus({ shipmentId: shipment.id, newStatus }); setVersion(value => value + 1); }
    catch (err) { setActionError(getApiErrorMessage(err)); }

  };
  const statusOptions = SHIPMENT_STATUSES.map(value => ({ value, label: enumLabel(value) }));
  return <section className="integration-panel customer-shipments">
    <header className="customer-shipments__header">
      <div><h3>Customer shipments <span className="customer-shipments__count">{total}</span></h3>
        <p>Track routes, delivery progress and shipment details.</p></div>
    </header>
    <div className="customer-shipments__filters">
      <FilterSelect label="Status" value={status} onChange={value => { setStatus(value); setPage(1); }} allLabel="All statuses" options={statusOptions} />
      <DatePresetFilter startDate={startDate} endDate={endDate} onChange={({ startDate, endDate }) => { setStartDate(startDate); setEndDate(endDate); setPage(1); }} />
    </div>
    {actionError && <p role="alert" className="integration-error">{actionError}</p>}
    {error ? <p role="alert" className="integration-error">{error} <Button onClick={() => setVersion(value => value + 1)}>Retry</Button></p>
      : loading ? <p role="status">Loading shipments...</p>
      : !rows.length ? <div className="customer-shipments__empty"><h4>No shipments match these filters.</h4><p>Try another status, date range or search.</p></div>
      : <div className="table-container"><table className="data-table"><thead><tr><th>Customer</th><th>Shipment</th><th>Route</th><th>Status</th><th>Created</th></tr></thead>
        <tbody>{rows.map(shipment => {
          const tracking = shipment.trackingId || shipment.tracking?.trackingId;
          const label = enumLabel(shipment.shipmentStatus);
          return <tr key={shipment.id}>
            <td data-label="Customer">{getCustomerDisplayName(shipment.user || {})}</td>
            <td data-label="Shipment"><Link className="customer-shipments__reference" title={shipment.id} aria-label={`View shipment ${shipment.id}`} to={`/shipments/${shipment.id}`}>{tracking || `Shipment ${shipment.id.slice(0, 8)}`}<span aria-hidden="true"> ↗</span></Link><small className="customer-shipments__secondary" title={shipment.id}>#{shipment.id.slice(0, 8)}</small></td>
            <td data-label="Route"><div className="customer-shipments__route"><span>{shipment.locationFrom}</span><span className="customer-shipments__secondary">↓ {shipment.locationTo}</span></div></td>
            <td data-label="Status">{can("shipments.manage") ? <StatusAction value={shipment.shipmentStatus.toLowerCase()} label={label} options={statusOptions} onChange={value => changeStatus(shipment, value)} /> : <span className="status-badge">{label}</span>}</td>
            <td data-label="Created"><span>{new Date(shipment.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span><small className="customer-shipments__secondary">{new Date(shipment.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</small></td>
          </tr>;
        })}</tbody></table></div>}
    {pages > 1 && <Pagination currentPage={page} totalPages={pages} totalItems={total} onPageChange={setPage} loading={loading} disabled={!!error} />}
  </section>;
}
